import { useEffect, useState, useMemo, useCallback } from 'react';
import axios from 'axios';
import { ENDPOINTS } from '../../../Utils/Endpoints';
import { Models } from '../../../Utils/ModelsLMS';
import { normalizeValue } from '../../../../../utils/Texts';
import AlertDialog from '../../Shared/AlertDialog';
import { isSessionSuperAdmin, getSessionEmpresaId } from '../../../../../utils/SessionUser';

const TITLETAB = 'Asignación de Carreras';

export default function CarreraEstudianteTab({ idEstudiante, onClose, onDirtyChange }) {
    const [allCarreras, setAllCarreras] = useState([]);
    const [storedAssignments, setStoredAssignments] = useState([]);
    const [currentAssigned, setCurrentAssigned] = useState([]);
    const [dragOverColumn, setDragOverColumn] = useState(null);
    const [saving, setSaving] = useState(false);
    const [query, setQuery] = useState('');
    const [alertConfig, setAlertConfig] = useState({ open: false, message: '', title: 'Información', variant: 'info' });

    const triggerAlert = (message, variant = 'error', title = 'Error') => {
        setAlertConfig({ open: true, message, variant, title });
    };

    const load = useCallback(async () => {
        // setLoading(true);
        try {
            const [resCarreras, resAssignments] = await Promise.all([
                axios.get(ENDPOINTS.carrerasHistorico),
                axios.get(ENDPOINTS.carreraEstudianteStored(idEstudiante))
            ]);

            const carreras = (resCarreras.data || []).map(Models.carrera);
            const myStored = (resAssignments.data || []).map(Models.carreraEstudiante);

            const myActive = myStored.filter(a => a.activo);

            setAllCarreras(carreras);
            setStoredAssignments(myStored);

            // Enriquecemos la lista actual con nombres de carrera y estado
            const enriched = myActive.map(a => {
                const c = carreras.find(x => Number(x.idCarrera) === Number(a.idCarrera));
                const empLabel = (c && c.empresaNombre) ? c.empresaNombre : 'General';
                const nombre = c ? `(${empLabel}): ${normalizeValue(c.nombre)}` : `Carrera #${a.idCarrera}`;
                return {
                    ...a,
                    nombreCarrera: c && !c.activado ? `${nombre} (Desactivado)` : nombre,
                    certificadoGenerado: a.certificadoGenerado // Ya viene del SP historico
                };
            });

            setCurrentAssigned(enriched);
        } catch (e) {
            console.error(e);
            triggerAlert('Error al cargar carreras');
        } finally {
            // setLoading(false);
        }
    }, [idEstudiante]);

    useEffect(() => {
        if (idEstudiante) load();
    }, [idEstudiante, load]);

    const availableCarreras = useMemo(() => {
        const assignedIds = new Set(currentAssigned.map(a => a.idCarrera));
        const isSuperAdmin = isSessionSuperAdmin();
        const sessionEmpresaId = getSessionEmpresaId();

        // Solo mostramos para asignar carreras que esten ACTIVAS y PUBLICADAS
        let available = allCarreras.filter(c => {
            // 1. No estar asignado
            if (assignedIds.has(c.idCarrera)) return false;
            // 2. Estar activado y publicado
            if (!c.activado || (c.publicado !== true && c.publicado !== 'true' && c.publicado !== 1 && c.publicado !== '1')) return false;
            // 3. Multi-tenant
            if (isSuperAdmin) return true;
            return c.idEmpresa === sessionEmpresaId || !c.idEmpresa || c.idEmpresa === 0;
        });

        if (query.trim()) {
            const q = query.trim().toLowerCase();
            available = available.filter(c => normalizeValue(c.nombre).toLowerCase().includes(q));
        }
        return available;
    }, [allCarreras, currentAssigned, query]);

    const hasChanges = useMemo(() => {
        const initialActive = storedAssignments.filter(a => a.activo);
        const initialIds = new Set(initialActive.map(a => a.idCarrera));
        if (initialIds.size !== currentAssigned.length) return true;
        return currentAssigned.some(a => !initialIds.has(a.idCarrera));
    }, [storedAssignments, currentAssigned]);

    useEffect(() => {
        if (onDirtyChange) {
            onDirtyChange(hasChanges);
        }
    }, [hasChanges, onDirtyChange]);

    // Drag and Drop
    const handleDragStart = (e, item, source) => {
        e.dataTransfer.setData('application/json', JSON.stringify({ item, source }));
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleDragOver = (e, targetColumn) => {
        e.preventDefault();
        if (dragOverColumn !== targetColumn) setDragOverColumn(targetColumn);
    };

    const handleDrop = (e, targetColumn) => {
        e.preventDefault();
        setDragOverColumn(null);

        const dataStr = e.dataTransfer.getData('application/json');
        if (!dataStr) return;

        const { item, source } = JSON.parse(dataStr);
        if (source === targetColumn) return;

        if (targetColumn === 'assigned') addLocal(item);
        else removeLocal(item);
    };

    const addLocal = (carrera) => {
        if (currentAssigned.some(a => a.idCarrera === carrera.idCarrera)) return;

        const existing = storedAssignments.find(a => Number(a.idCarrera) === Number(carrera.idCarrera));

        const newAssignment = {
            idCarreraEstudiante: existing ? existing.idCarreraEstudiante : 0,
            idCarrera: carrera.idCarrera,
            idEstudiante: Number(idEstudiante),
            fechaAsignacion: existing ? existing.fechaAsignacion : new Date().getFullYear(),
            activo: true,
            nombreCarrera: `(${(carrera.empresaNombre) || 'General'}): ${normalizeValue(carrera.nombre)}`,
            certificadoGenerado: existing ? existing.certificadoGenerado : false
        };
        setCurrentAssigned(prev => [...prev, newAssignment]);
    };

    const removeLocal = (assignment) => {
        // RESTRICCION: No quitar si ya tiene certificado
        if (assignment.certificadoGenerado) {
            triggerAlert(`La carrera "${assignment.nombreCarrera}" no se puede remover porque el estudiante ya obtuvo su certificado.`, 'warning', 'Acción denegada');
            return;
        }

        setCurrentAssigned(prev => prev.filter(a => a.idCarrera !== assignment.idCarrera));
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const initialActive = storedAssignments.filter(a => a.activo);
            const initialIds = new Set(initialActive.map(a => a.idCarrera));
            const currentIds = new Set(currentAssigned.map(a => a.idCarrera));

            const toAdd = currentAssigned.filter(a => !initialIds.has(a.idCarrera));
            const toRemove = initialActive.filter(a => !currentIds.has(a.idCarrera));

            const promises = [];

            // Altas / Reactivaciones
            for (const item of toAdd) {
                if (item.idCarreraEstudiante > 0) {
                    // Ya existía en BD pero estaba inactivo => Actualizar Activo = 1
                    promises.push(axios.put(ENDPOINTS.carreraEstudiante, { ...item, activo: true }));
                } else {
                    // Completamente nuevo
                    promises.push(axios.post(ENDPOINTS.carreraEstudiante, { ...item, activo: true }));
                }
            }

            // Bajas lógicas
            for (const item of toRemove) {
                promises.push(axios.delete(ENDPOINTS.carreraEstudianteById(item.idCarreraEstudiante)));
            }

            await Promise.all(promises);
            await load();
            triggerAlert('Cambios guardados correctamente', 'success', 'Éxito');
        } catch (e) {
            console.error(e);
            triggerAlert('Error al guardar cambios');
        } finally {
            setSaving(false);
        }
    };

    const handleCancel = () => {
        const initialActive = storedAssignments.filter(a => a.activo);
        const enriched = initialActive.map(a => {
            const c = allCarreras.find(x => Number(x.idCarrera) === Number(a.idCarrera));
            const nombre = c ? normalizeValue(c.nombre) : `Carrera #${a.idCarrera}`;
            return {
                ...a,
                nombreCarrera: c && !c.activado ? `${nombre} (Desactivado)` : nombre,
                certificadoGenerado: a.certificadoGenerado
            };
        });
        setCurrentAssigned(enriched);
    };

    return (
        <div className="mini-crud">
            <div className="mini-topbar">
                <div style={{ width: '100%' }}>
                    <h3 className="roles-title" style={{ margin: 0 }}>{TITLETAB}</h3>
                    <div style={{ fontSize: '0.85rem', color: '#666', marginTop: '4px' }}>
                        Arrastra las carreras para asignar o desasignar al estudiante. No olvides <b>Guardar</b>.
                    </div>
                </div>
            </div>

            <div style={{ marginBottom: '12px' }}>
                <input
                    className="roles-search"
                    placeholder="Filtrar carreras disponibles..."
                    value={query}
                    onChange={e => setQuery(e.target.value)}
                    style={{ width: '100%', maxWidth: '400px' }}
                />
            </div>

            <div className="dnd-container">
                {/* Disponibles */}
                <div
                    className={`dnd-column ${dragOverColumn === 'available' ? 'drag-over' : ''}`}
                    onDragOver={e => handleDragOver(e, 'available')}
                    onDrop={e => handleDrop(e, 'available')}
                >
                    <div className="dnd-header">Carreras Disponibles</div>
                    {availableCarreras.length === 0 ? (
                        <div className="dnd-empty">{query ? 'Sin resultados' : 'No hay más carreras'}</div>
                    ) : (
                        availableCarreras.map(c => (
                            <div
                                key={c.idCarrera}
                                className="dnd-item"
                                draggable={!saving}
                                onDragStart={e => handleDragStart(e, c, 'available')}
                            >
                                <span>{`(${c.empresaNombre || 'General'}): ${normalizeValue(c.nombre)}`}</span>
                                <button
                                    onClick={() => addLocal(c)}
                                    style={{ background: 'transparent', border: 'none', color: '#28a745', fontSize: '1.2rem', cursor: 'pointer' }}
                                >＋</button>
                            </div>
                        ))
                    )}
                </div>

                {/* Asignadas */}
                <div
                    className={`dnd-column ${dragOverColumn === 'assigned' ? 'drag-over' : ''}`}
                    onDragOver={e => handleDragOver(e, 'assigned')}
                    onDrop={e => handleDrop(e, 'assigned')}
                >
                    <div className="dnd-header">Carreras Asignadas</div>
                    {currentAssigned.length === 0 ? (
                        <div className="dnd-empty">Arrastra carreras aquí</div>
                    ) : (
                        currentAssigned.map(a => (
                            <div
                                key={a.idCarrera}
                                className="dnd-item"
                                draggable={!saving}
                                onDragStart={e => handleDragStart(e, a, 'assigned')}
                            >
                                <span style={{ color: a.certificadoGenerado ? '#94a3b8' : 'inherit' }}>{a.nombreCarrera}</span>
                                <button
                                    onClick={() => removeLocal(a)}
                                    style={{ background: 'transparent', border: 'none', color: '#dc3545', fontSize: '1.2rem', cursor: 'pointer' }}
                                >×</button>
                            </div>
                        ))
                    )}
                </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '1rem' }}>
                <button className="roles-btn" onClick={handleCancel} disabled={!hasChanges || saving}>Descartar</button>
                <button
                    className="btn-action"
                    onClick={handleSave}
                    disabled={!hasChanges || saving}
                    style={{ opacity: (!hasChanges || saving) ? 0.6 : 1 }}
                >
                    {saving ? 'Guardando...' : 'Guardar Cambios'}
                </button>
            </div>

            <AlertDialog
                open={alertConfig.open}
                title={alertConfig.title}
                message={alertConfig.message}
                variant={alertConfig.variant}
                onClose={() => setAlertConfig({ ...alertConfig, open: false })}
            />
        </div>
    );
}
